export const ACTIVE_CHAT_STATES = new Set(['received', 'queued', 'processing', 'streaming']);
export const CHAT_LIST_FILTERS = ['all', 'recent', 'with-images', 'travel', 'scan', 'list', 'generating'];
export const TERMINAL_CHAT_STATES = new Set(['done', 'cancelled', 'error', 'idle']);

const CHAT_STATE_PRIORITY = {
  received: 10,
  queued: 20,
  processing: 30,
  streaming: 40,
  done: 50,
  cancelled: 50,
  error: 50,
  idle: 60,
};

function normalizeForSearch(value) {
  return (value || '').trim().toLocaleLowerCase('ru-RU');
}

function toTimestamp(value) {
  const parsed = Date.parse(value || 0);
  return Number.isNaN(parsed) ? 0 : parsed;
}

// Порядок ленты (этап 11) — те же три ключа, что в SQL списка чатов
// (repository.list_chats): закреплённые вперёд, среди них последний закреплённый
// сверху, дальше по времени последнего сообщения. Клиент пересортировывает всё,
// что приходит с сервера, поэтому правило обязано совпадать байт в байт.
export function normalizeChats(items = []) {
  return [...items].sort((left, right) => {
    const leftPin = toTimestamp(left.pinnedAt || 0);
    const rightPin = toTimestamp(right.pinnedAt || 0);
    if (leftPin !== rightPin) {
      return rightPin - leftPin;
    }
    const rightTs = toTimestamp(right.updatedAt || right.createdAt || 0);
    const leftTs = toTimestamp(left.updatedAt || left.createdAt || 0);
    return rightTs - leftTs;
  });
}

// Две группы ленты: закреплённые и остальные. Порядок внутри групп уже задан
// normalizeChats — здесь только разделение, чтобы список отрисовал секцию.
export function splitPinnedChats(items = []) {
  const pinned = [];
  const rest = [];
  for (const item of normalizeChats(items)) {
    (item.pinnedAt ? pinned : rest).push(item);
  }
  return { pinned, rest };
}

export function filterChatsByQuery(items = [], query = '') {
  const normalizedQuery = normalizeForSearch(query);
  if (!normalizedQuery) {
    return normalizeChats(items);
  }

  return normalizeChats(
    items.filter((item) => {
      const haystacks = [
        item.title || 'Новый чат',
        item.lastMessagePreview || '',
      ];

      return haystacks.some((value) => normalizeForSearch(value).includes(normalizedQuery));
    })
  );
}

export function filterChatsByPreset(items = [], preset = 'all', activities = {}, referenceTime = Date.now()) {
  if (preset === 'all') {
    return normalizeChats(items);
  }

  return normalizeChats(items.filter((item) => {
    if (preset === 'with-images') {
      return Boolean(item.hasImages);
    }

    if (preset === 'generating') {
      return isGeneratingState(activities[item.chatId]?.state);
    }

    // Типы чатов (шторка «Показать»): путевой определяется автоматически,
    // скан и список — выбираются при создании.
    if (preset === 'travel' || preset === 'scan' || preset === 'list') {
      return item.chatType === preset;
    }

    if (preset === 'recent') {
      return referenceTime - toTimestamp(item.updatedAt || item.createdAt) <= 1000 * 60 * 60 * 24 * 3;
    }

    return true;
  }));
}

// Дата в строке списка (1.27.2): по календарным дням, а не «меньше суток назад» —
// иначе вчерашние 23:50 утром показывались временем, как сегодняшние.
export function formatChatListDate(isoString, { now = new Date(), yesterdayLabel = 'вчера' } = {}) {
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const startOfDay = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const daysAgo = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (daysAgo <= 0) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }
  if (daysAgo === 1) {
    return yesterdayLabel;
  }
  if (daysAgo < 7) {
    return date.toLocaleDateString('ru-RU', { weekday: 'short' });
  }
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function getChatPreviewLabel(chat = {}) {
  if (chat.lastMessageRole === 'user') {
    return 'Вы';
  }
  if (chat.lastMessageRole === 'assistant') {
    return 'Ответ';
  }
  if (chat.lastMessageRole === 'system') {
    return 'Система';
  }
  return 'Контекст';
}

// «Прочитано всеми» (этап 7.3): своё user-сообщение, которое все ОСТАЛЬНЫЕ
// участники отметили прочитанным (их lastReadTs >= ts сообщения). В личном чате
// «остальных» нет — галочки не бывает. Строковое сравнение ISO-ts корректно:
// ts выдаёт backend в едином формате.
export function isReadByAll(message, members = [], myUserId = null) {
  if (!message || message.role !== 'user' || !message.ts) {
    return false;
  }
  if (message.userId && myUserId && message.userId !== myUserId) {
    return false;
  }
  const others = (members || []).filter((member) => member.userId !== myUserId);
  if (!others.length) {
    return false;
  }
  return others.every((member) => Boolean(member.lastReadTs) && member.lastReadTs >= message.ts);
}

// Патч состава чата в списке после события 'read' — без перезагрузки списка.
export function applyReadMarkToChats(chats = [], payload = {}) {
  if (!payload?.chatId || !payload?.userId) {
    return chats;
  }
  let changed = false;
  const next = chats.map((chat) => {
    if (chat.chatId !== payload.chatId || !Array.isArray(chat.members)) {
      return chat;
    }
    const members = chat.members.map((member) => {
      if (member.userId !== payload.userId) {
        return member;
      }
      if (member.lastReadTs && payload.lastReadTs && member.lastReadTs >= payload.lastReadTs) {
        return member;
      }
      changed = true;
      return { ...member, lastReadTs: payload.lastReadTs };
    });
    return changed ? { ...chat, members } : chat;
  });
  return changed ? next : chats;
}

// Упоминание Петровича в тексте — то же правило, что на backend
// (detect_assistant_mention): @ + имя целым словом, без регистра.
// \w в JS не покрывает кириллицу — границы слова через \p{L}/\p{N} с флагом u.
const ASSISTANT_MENTION_RE = /(^|[^\p{L}\p{N}_@])@(петрович|petrovich)(?![\p{L}\p{N}_@])/iu;

export function hasAssistantMention(text = '') {
  return ASSISTANT_MENTION_RE.test(text || '');
}

// Быстрый вызов (чип/long-press): подставить @Петрович, если его ещё нет.
export function withAssistantMention(text = '', token = '@Петрович') {
  const normalized = (text || '').trim();
  if (hasAssistantMention(normalized)) {
    return normalized;
  }
  return normalized ? `${token} ${normalized}` : token;
}

export function isGeneratingState(state) {
  return ACTIVE_CHAT_STATES.has(state);
}

export function buildChatActivity(chatId, current = {}, patch = {}) {
  return {
    chatId,
    requestId: patch.requestId ?? current.requestId ?? null,
    model: patch.model ?? current.model ?? null,
    state: patch.state ?? current.state ?? 'idle',
    message: patch.message ?? current.message ?? null,
    ts: patch.ts ?? current.ts ?? null,
    partialText: patch.partialText ?? current.partialText ?? '',
    partialSeq: patch.partialSeq ?? current.partialSeq ?? 0,
    tech: patch.tech ?? current.tech ?? null,
    responseText: patch.responseText ?? current.responseText ?? null,
    error: patch.error ?? current.error ?? null,
  };
}

function getStatePriority(state) {
  return CHAT_STATE_PRIORITY[state] ?? 0;
}

function isSameRequest(current, payload) {
  return Boolean(current?.requestId && payload?.requestId && current.requestId === payload.requestId);
}

function shouldIgnoreForeignRequest(current, payload) {
  if (!current?.requestId || !payload?.requestId || current.requestId === payload.requestId) {
    return false;
  }

  return isGeneratingState(current.state);
}

function shouldIgnoreStatusTransition(current, payload) {
  if (!isSameRequest(current, payload)) {
    return false;
  }

  if (payload.state === 'idle') {
    return false;
  }

  if (TERMINAL_CHAT_STATES.has(current.state)) {
    return true;
  }

  return getStatePriority(payload.state) < getStatePriority(current.state);
}

export function applyStatusToActivities(activities, payload) {
  if (!payload?.chatId || !payload?.requestId) {
    return activities;
  }

  const current = activities[payload.chatId] || {};
  if (shouldIgnoreForeignRequest(current, payload) || shouldIgnoreStatusTransition(current, payload)) {
    return activities;
  }

  const next = buildChatActivity(payload.chatId, current, {
    requestId: payload.requestId,
    model: payload.model,
    state: payload.state,
    message: payload.message || null,
    ts: payload.ts || current.ts || null,
    error: payload.state === 'error' ? payload.error || payload.message || 'Ошибка генерации' : null,
  });

  if (payload.state === 'idle') {
    const { [payload.chatId]: _removed, ...rest } = activities;
    return rest;
  }

  return {
    ...activities,
    [payload.chatId]: next,
  };
}

export function applyPartialToActivities(activities, payload) {
  if (!payload?.chatId || !payload?.requestId) {
    return activities;
  }

  const current = activities[payload.chatId] || {};
  if (shouldIgnoreForeignRequest(current, payload)) {
    return activities;
  }

  if (isSameRequest(current, payload) && TERMINAL_CHAT_STATES.has(current.state)) {
    return activities;
  }

  const nextSeq = payload.seq ?? 0;
  const sameRequest = current.requestId === payload.requestId;
  // seq считается заново на КАЖДОЙ попытке генерации, а один и тот же requestId
  // переигрывается штатно: recovery доигрывает запрос, прерванный обрывом брокера
  // или рестартом backend. Пришёл seq=1 поверх накопленного — это новая попытка:
  // старый обрывок текста выбрасываем, иначе чанки отсеивались бы как «старые» и
  // чат навсегда застывал на «Печатает…».
  const restarted = sameRequest && nextSeq <= 1 && (current.partialSeq ?? 0) > 1;
  if (sameRequest && !restarted && (current.partialSeq ?? 0) >= nextSeq) {
    return activities;
  }

  return {
    ...activities,
    [payload.chatId]: buildChatActivity(payload.chatId, current, {
      requestId: payload.requestId,
      model: payload.model,
      state: 'streaming',
      partialText: `${sameRequest && !restarted ? current.partialText || '' : ''}${payload.delta || ''}`,
      partialSeq: nextSeq,
      ts: payload.ts || current.ts || null,
      responseText: null,
      error: null,
    }),
  };
}

export function applyResponseToActivities(activities, payload) {
  if (!payload?.chatId || !payload?.requestId) {
    return activities;
  }

  const current = activities[payload.chatId] || {};
  if (shouldIgnoreForeignRequest(current, payload)) {
    return activities;
  }

  if (isSameRequest(current, payload) && current.state === 'done' && current.responseText) {
    return activities;
  }

  // 1.27.0: недописанный ответ (stopReason) — это не «ответ получен». Итог
  // отмены или ошибки, если он уже пришёл статусом, остаётся со своим текстом;
  // пузырь стрима при этом гаснет — обрывок теперь обычное сообщение ленты.
  if (payload.stopReason) {
    const keepOutcome = isSameRequest(current, payload) && ['cancelled', 'error'].includes(current.state);
    const outcome = payload.stopReason === 'error' ? 'error' : 'cancelled';
    return {
      ...activities,
      [payload.chatId]: buildChatActivity(payload.chatId, current, {
        requestId: payload.requestId,
        model: payload.model,
        state: keepOutcome ? current.state : outcome,
        partialText: '',
        partialSeq: 0,
        responseText: payload.text || '',
        ts: payload.ts || current.ts || null,
        error: keepOutcome ? current.error : (outcome === 'error' ? 'Ошибка генерации' : null),
      }),
    };
  }

  return {
    ...activities,
    [payload.chatId]: buildChatActivity(payload.chatId, current, {
      requestId: payload.requestId,
      model: payload.model,
      state: 'done',
      partialText: '',
      partialSeq: 0,
      responseText: payload.text || '',
      ts: payload.ts || current.ts || null,
      error: null,
    }),
  };
}

// «Ещё раз» и «Повторить» (1.27.0): тот же requestId отвечается заново. Итог
// прошлой попытки (done, cancelled, error) у запроса терминальный — статусы новой
// попытки иначе отсеивались бы как запоздавшие. Активность начинается заново.
export function beginRegenActivity(activities, chatId, requestId, model = null) {
  if (!chatId || !requestId) {
    return activities;
  }
  return {
    ...activities,
    [chatId]: buildChatActivity(chatId, {}, { requestId, model, state: 'received' }),
  };
}

export function applyTechToActivities(activities, payload) {
  if (!payload?.chatId || !payload?.requestId) {
    return activities;
  }

  const current = activities[payload.chatId] || {};
  if (shouldIgnoreForeignRequest(current, payload)) {
    return activities;
  }

  return {
    ...activities,
    [payload.chatId]: buildChatActivity(payload.chatId, current, {
      requestId: payload.requestId,
      model: payload.model,
      tech: payload,
      ts: payload.ts || current.ts || null,
    }),
  };
}

// Шторка «+ Новый» (этап 24): типы чата плюс подменю «Из библиотеки…» с последними
// шаблонами и «Библиотека…». Подменю, а не секция: шторка иначе выросла бы до девяти строк.
export const TEMPLATE_SHEET_LIMIT = 5;

export function buildNewChatActions(baseActions, templates, text) {
  const list = Array.isArray(templates) ? templates.slice(0, TEMPLATE_SHEET_LIMIT) : [];
  const children = [
    ...list.map((template) => ({ id: `template:${template.templateId}`, label: template.name, icon: 'draft' })),
    { id: 'templates-library', label: text.openLibrary, icon: 'settings' },
  ];
  return [...baseActions, { id: 'from-library', label: text.fromLibrary, icon: 'draft', children }];
}

export function templateIdFromAction(actionId) {
  return typeof actionId === 'string' && actionId.startsWith('template:') ? actionId.slice('template:'.length) : null;
}
